import React from 'react'
import { ExternalLink, Github, Sparkles, Heart, Zap, Users, ShoppingCart } from 'lucide-react'

const Projects = () => {
  const projects = [
    {
      title: "冥想社区工具",
      description: "专注于心理健康的冥想社区平台，帮助用户建立正念习惯，提升心理健康水平。融合了现代科技与古老智慧，为用户提供个性化的冥想体验。",
      url: "https://sxyz.space/",
      icon: Heart,
      color: "from-pink-500 to-purple-600",
      tags: ["Vue.js", "Node", "心理健康", "社区"],
      features: [
        "个性化冥想推荐",
        "社区交流互动",
        "进度追踪记录",
        "专业指导内容"
      ]
    },
    {
      title: "东玄上香祈福",
      description: "传统文化与现代技术结合的祈福平台，让用户在数字世界中体验传统祈福文化。通过技术手段传承和发扬中华传统文化的精神内涵。",
      url: "https://temple.polarjordan.xyz/",
      icon: Sparkles,
      color: "from-yellow-500 to-orange-600",
      tags: ["Next.js", "传统文化", "Web3", "祈福"],
      features: [
        "虚拟上香体验",
        "传统祈福仪式",
        "文化知识普及",
        "心愿记录分享"
      ]
    },
    {
      title: "西玄塔罗占卜",
      description: "融合东西方玄学的数字化占卜工具，运用AI技术为用户提供个性化的塔罗牌解读。将古老的占卜艺术与现代科技完美结合。",
      url: "https://tarot.polarjordan.xyz/",
      icon: Zap,
      color: "from-purple-500 to-blue-600",
      tags: ["Next.js", "AI", "塔罗占卜", "玄学"],
      features: [
        "AI智能解读",
        "多种牌阵选择",
        "个性化建议",
        "历史记录追踪"
      ]
    },
    {
      title: "校园模拟游戏",
      description: "初中生的生活RPG游戏，使用AI大模型驱动的智能NPC对话体验真实的初中校园生活。通过沉浸式游戏体验，让玩家在虚拟世界中体验成长与友情。",
      url: "https://campus.polarjordan.xyz/",
      icon: Users,
      color: "from-green-500 to-teal-600",
      tags: ["游戏开发", "AI", "RPG", "教育"],
      features: [
        "AI智能NPC对话",
        "真实校园生活模拟",
        "角色成长系统",
        "社交互动体验"
      ]
    },
    {
      title: "电商交易平台",
      description: "AI时代去中心化的智能体Agent自动化交易平台，AI协作的市场竞价模型。作为用户，可以让多种Agent组队协作并通过竞价来获取服务；作为服务商，可以通过配置多类Agent并获取自动化的收益。",
      url: "https://polarjordan.online/",
      icon: ShoppingCart,
      color: "from-blue-500 to-indigo-600",
      tags: ["AI Agent", "去中心化", "自动化交易"],
      features: [
        "AI Agent协作",
        "智能竞价模型",
        "自动化交易",
        "去中心化平台"
      ]
    }
  ]

  return (
    <section id="projects" className="section-padding bg-gray-50">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="gradient-text mb-4">项目展示</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            通过黑客松比赛探索技术创新，用代码连接传统与现代，创造有温度的数字产品
          </p>
        </div>

        <div className="grid lg:grid-cols-1 gap-12">
          {projects.map((project, index) => (
            <div key={index} className="card hover:shadow-2xl transition-all duration-500 overflow-hidden">
              <div className="flex flex-col lg:flex-row items-start gap-8">
                {/* 项目图标和基本信息 */}
                <div className="flex-shrink-0">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${project.color} flex items-center justify-center mb-4`}>
                    <project.icon size={40} className="text-white" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* 项目详情 */}
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 mb-2 sm:mb-0">{project.title}</h3>
                    <div className="flex gap-3">
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 btn-primary text-sm hover:scale-105 transform transition-all duration-300"
                      >
                        <ExternalLink size={16} />
                        在线体验
                      </a>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-6 leading-relaxed">{project.description}</p>

                  {/* 功能特点 */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">核心功能</h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {project.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 更多项目提示 */}
        <div className="text-center mt-16">
          <div className="card inline-block">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                <Github size={24} className="text-white" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-gray-800">更多项目正在路上</h4>
                <p className="text-gray-600 text-sm">持续探索AI时代的技术创新与应用</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Projects 